/* eslint-disable @typescript-eslint/no-explicit-any -- react-hook-form useFieldArray/Control types require any for array field names */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { tidbitApi } from '@/services/learningManagementApi';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import type {
  Tidbit,
  TidbitRequest,
  TidbitCategory,
  TidbitType,
  TidbitInterestLevel,
  TidbitStatus,
  TidbitMediaType,
  TidbitDifficultyLevel,
  GenerationSource,
  TargetAudience,
} from '@/types/learningManagement';

const tidbitSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  skillId: z.string().min(1, 'Skill ID is required'),
  description: z.string().min(0).max(2000, 'Description must be less than 2000 characters'),
  content: z.string().min(0).max(5000, 'Content must be less than 5000 characters'),
  preview: z.string().max(500).optional(),
  category: z.enum(['TECHNOLOGY', 'SCIENCE', 'HEALTH', 'BUSINESS', 'EDUCATION', 'LIFESTYLE', 'ENTERTAINMENT', 'ENVIRONMENT', 'POLITICS', 'CULTURE', 'HISTORY', 'ART', 'SPORTS', 'TRAVEL']),
  tidbitType: z.enum(['FACT', 'INSIGHT', 'TREND', 'BREAKTHROUGH', 'HISTORICAL', 'STATISTICAL', 'BEST_PRACTICE', 'CASE_STUDY', 'QUOTE', 'TIP', 'HACK', 'NEWS', 'RESEARCH_FINDING']),
  subCategory: z.string().max(100).optional(),
  tags: z.array(z.string().max(50)).optional(),
  interestLevel: z.enum(['LOW', 'MODERATE', 'HIGH', 'FASCINATING', 'MIND_BLOWING']),
  status: z.enum(['CONSUMED', 'VIEWED', 'DELIVERED', 'DRAFT', 'PUBLISHED', 'ACTIVE', 'FEATURED', 'ARCHIVED', 'EXPIRED', 'UNDER_REVIEW']),
  consumptionTimeMinutes: z.number().min(1).optional(),
  mediaType: z.enum(['TEXT', 'IMAGE', 'VIDEO', 'AUDIO', 'INFOGRAPHIC', 'INTERACTIVE', 'MIXED_MEDIA']).optional(),
  resources: z.array(z.string()),
  targetAudience: z.enum(['GENERAL', 'BEGINNERS', 'INTERMEDIATE', 'ADVANCED', 'PROFESSIONALS', 'ACADEMICS', 'PRACTITIONERS', 'CUSTOM_SEGMENT']).optional(),
  difficultyLevel: z.enum(['ELEMENTARY', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT']).optional(),
  publicationDate: z.string().optional(),
  expirationDate: z.string().optional(),
  personalizationParams: z.record(z.string(), z.unknown()).optional(),
  tidbitGenerated: z.enum(['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED']),
  authorInfo: z.object({
    authorId: z.string().optional(),
    authorName: z.string().optional(),
    authorEmail: z.string().email().optional(),
  }).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

type TidbitFormData = z.infer<typeof tidbitSchema>;

const CATEGORIES: TidbitCategory[] = ['TECHNOLOGY', 'SCIENCE', 'HEALTH', 'BUSINESS', 'EDUCATION', 'LIFESTYLE', 'ENTERTAINMENT', 'ENVIRONMENT', 'POLITICS', 'CULTURE', 'HISTORY', 'ART', 'SPORTS', 'TRAVEL'];
const TIDBIT_TYPES: TidbitType[] = ['FACT', 'INSIGHT', 'TREND', 'BREAKTHROUGH', 'HISTORICAL', 'STATISTICAL', 'BEST_PRACTICE', 'CASE_STUDY', 'QUOTE', 'TIP', 'HACK', 'NEWS', 'RESEARCH_FINDING'];
const INTEREST_LEVELS: TidbitInterestLevel[] = ['LOW', 'MODERATE', 'HIGH', 'FASCINATING', 'MIND_BLOWING'];
const STATUSES: TidbitStatus[] = ['CONSUMED', 'VIEWED', 'DELIVERED', 'DRAFT', 'PUBLISHED', 'ACTIVE', 'FEATURED', 'ARCHIVED', 'EXPIRED', 'UNDER_REVIEW'];
const MEDIA_TYPES: TidbitMediaType[] = ['TEXT', 'IMAGE', 'VIDEO', 'AUDIO', 'INFOGRAPHIC', 'INTERACTIVE', 'MIXED_MEDIA'];
const DIFFICULTY_LEVELS: TidbitDifficultyLevel[] = ['ELEMENTARY', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT'];
const TARGET_AUDIENCES: TargetAudience[] = ['GENERAL', 'BEGINNERS', 'INTERMEDIATE', 'ADVANCED', 'PROFESSIONALS', 'ACADEMICS', 'PRACTITIONERS', 'CUSTOM_SEGMENT'];
const GENERATION_SOURCES: GenerationSource[] = ['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED'];

export const TidbitFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [skills, setSkills] = useState<Array<{ id?: string; name: string }>>([]);
  const { hasPermission } = useAdminAuth();

  const form = useForm<TidbitFormData>({
    resolver: zodResolver(tidbitSchema),
    defaultValues: {
      title: '',
      skillId: '',
      description: '',
      content: '',
      preview: '',
      category: 'TECHNOLOGY',
      tidbitType: 'FACT',
      subCategory: '',
      interestLevel: 'MODERATE',
      status: 'DRAFT',
      consumptionTimeMinutes: undefined,
      mediaType: undefined,
      tags: [],
      resources: [],
      targetAudience: undefined,
      difficultyLevel: undefined,
      publicationDate: undefined,
      expirationDate: undefined,
      personalizationParams: undefined,
      tidbitGenerated: 'USER_CUSTOMIZED',
      authorInfo: undefined,
      metadata: undefined,
    },
  });

  const { fields: resourceFields, append: appendResource, remove: removeResource } = useFieldArray({
    control: form.control as any,
    name: 'resources' as any,
  });

  useEffect(() => {
    // Load skills for dropdown
    skillApi.list({ page: 0, size: 1000 }).then((response) => {
      setSkills((response.content || []).map((s) => ({ id: s.id, name: s.name })));
    }).catch((error) => {
      console.error('Error loading skills:', error);
    });
  }, []);

  // Fetch tidbit data for edit mode
  const {
    data: _tidbitData,
    isLoading: isLoadingTidbit,
    error: tidbitError,
  } = useEntity<Tidbit>({
    fetchFn: (tidbitId: string) => tidbitApi.getById(tidbitId),
    entityId: id,
    entityName: 'tidbit',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        title: data.title,
        skillId: data.skillId || '',
        description: data.description || '',
        content: data.content,
        preview: data.preview || '',
        category: data.category,
        tidbitType: data.tidbitType,
        subCategory: data.subCategory || '',
        interestLevel: data.interestLevel,
        status: data.status,
        consumptionTimeMinutes: data.consumptionTimeMinutes,
        mediaType: data.mediaType,
        tags: data.tags || [],
        resources: data.resources || [],
        targetAudience: data.targetAudience,
        difficultyLevel: data.difficultyLevel,
        publicationDate: data.publicationDate,
        expirationDate: data.expirationDate,
        personalizationParams: data.personalizationParams,
        tidbitGenerated: data.tidbitGenerated,
        authorInfo: data.authorInfo,
        metadata: data.metadata,
      } as any);
    },
    onError: () => {
      navigate('/admin/tidbits');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: TidbitFormData) => {
      const payload: TidbitRequest = {
        ...data,
        tags: data.tags || [],
      };
      return await tidbitApi.create(payload);
    },
    entityName: 'tidbit',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/tidbits');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: TidbitFormData) => {
      if (!id) throw new Error('Tidbit ID is required');
      const payload: TidbitRequest = {
        ...data,
        tags: data.tags || [],
      };
      return await tidbitApi.update(id, payload);
    },
    entityName: 'tidbit',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/tidbits');
    },
  });

  const onSubmit = async (data: TidbitFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingTidbit;
  const mutation = isEdit ? updateMutation : createMutation;

  const isSystemDefined = form.watch('tidbitGenerated') === 'SYSTEM_DEFINED';
  const canModifyGeneration = !isEdit || !isSystemDefined || hasPermission('system_admin', 'tidbit');

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading tidbit..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && tidbitError) {
    return <ErrorState error={tidbitError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/tidbits">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Tidbit' : 'Create Tidbit'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update tidbit information' : 'Create a new tidbit'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
          console.error('Form validation errors:', errors);
        })} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the tidbit</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter tidbit title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter tidbit description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter tidbit content"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="preview"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preview</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter tidbit preview (max 500 characters)" {...field} />
                    </FormControl>
                    <FormDescription>Brief preview text for the tidbit</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="skillId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Skill ID *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select skill" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {skills.map((skill) => (
                          <SelectItem key={skill.id} value={skill.id || ''}>
                            {skill.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>Select the skill this tidbit is associated with</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tidbitType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tidbit Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select tidbit type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TIDBIT_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="interestLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Interest Level *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select difficulty" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {INTEREST_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mediaType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Media Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select media type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {MEDIA_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="subCategory"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sub Category</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter sub category (optional)" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="targetAudience"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Target Audience</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select target audience" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TARGET_AUDIENCES.map((audience) => (
                            <SelectItem key={audience} value={audience}>
                              {audience.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="difficultyLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Difficulty Level</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select difficulty level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {DIFFICULTY_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="publicationDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Publication Date</FormLabel>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          {...field}
                          value={field.value ? new Date(field.value).toISOString().slice(0, 16) : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value ? new Date(value).toISOString() : undefined);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="expirationDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiration Date</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        {...field}
                        value={field.value ? new Date(field.value).toISOString().slice(0, 16) : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value ? new Date(value).toISOString() : undefined);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <div>
                  <Label>Resources</Label>
                  <div className="space-y-2 mt-2">
                    {resourceFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          placeholder="Enter resource URL or description"
                          value={form.watch(`resources.${index}`) || ''}
                          onChange={(e) => {
                            const current = form.getValues('resources') || [];
                            current[index] = e.target.value;
                            form.setValue('resources', current);
                          }}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => removeResource(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendResource('')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Resource
                    </Button>
                  </div>
                </div>
              </div>

              <FormField
                control={form.control}
                name="consumptionTimeMinutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Consumption Time (Minutes)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        placeholder="Enter minutes"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter tags separated by commas"
                        value={field.value?.join(', ') || ''}
                        onChange={(e) => {
                          const tags = e.target.value
                            .split(',')
                            .map((t) => t.trim())
                            .filter(Boolean);
                          field.onChange(tags);
                        }}
                      />
                    </FormControl>
                    <FormDescription>Separate multiple tags with commas</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tidbitGenerated"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Generation Source *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={!canModifyGeneration}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select generation source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {GENERATION_SOURCES.map((source) => (
                          <SelectItem key={source} value={source}>
                            {source.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {!canModifyGeneration && 'Cannot change generation source for system-defined tidbits'}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/tidbits">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default TidbitFormPage;
